import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import ProductIconContainer from 'apex-web/lib/components/common/ProductIcon/ProductIconContainer';

const SwapArrowsIcon = () => (
  <svg
    className="dashboard-swap-pair__swap-icon-svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"
    />
  </svg>
);

const DashboardSwapPairPanel = (
  {
    instruments,
    selectedInstrument,
    onInstrumentSelect,
    product1,
    product2,
    quantityDisplay,
    priceDisplay,
    onToggleSide,
    isBuySide
  },
  context
) => {
  const t = context.t;
  const listboxIdRef = useRef(
    `dashboard-swap-instrument-${Math.random()
      .toString(36)
      .slice(2, 11)}`
  );
  const listboxId = listboxIdRef.current;

  const comboboxRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState(null);

  const hasInstrument =
    selectedInstrument &&
    selectedInstrument.InstrumentId != null &&
    !Number.isNaN(Number(selectedInstrument.InstrumentId));

  const updateMenuPlacement = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - 8;
    setMenuPlacement({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 12 * 14),
      maxHeight: Math.min(280, Math.max(120, spaceBelow))
    });
  }, []);

  useEffect(() => {
    if (!instruments.length) return;
    const preSelected = instruments.find(
      i => i.InstrumentId === selectedInstrument.InstrumentId
    );
    if (preSelected) return;
    onInstrumentSelect(instruments[0].InstrumentId);
  }, [instruments, selectedInstrument.InstrumentId, onInstrumentSelect]);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPlacement(null);
      return;
    }
    updateMenuPlacement();
  }, [menuOpen, updateMenuPlacement, selectedInstrument.InstrumentId]);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = e => {
      const t = e.target;
      if (
        comboboxRef.current &&
        comboboxRef.current.contains(t)
      ) {
        return;
      }
      if (menuRef.current && menuRef.current.contains(t)) {
        return;
      }
      setMenuOpen(false);
    };

    const onKeyDown = e => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    const onReposition = () => updateMenuPlacement();

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, {
      passive: true
    });
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [menuOpen, updateMenuPlacement]);

  if (!instruments.length) {
    return null;
  }

  const p1Name = product1?.ProductFullName || selectedInstrument.Product1FullName;
  const p2Name = product2?.ProductFullName || selectedInstrument.Product2FullName;
  const p1Sym = product1?.Product || selectedInstrument.Product1Symbol;
  const p2Sym = product2?.Product || selectedInstrument.Product2Symbol;

  const pairLabel = hasInstrument
    ? `${selectedInstrument.Product1Symbol} / ${selectedInstrument.Product2Symbol}`
    : `${instruments[0].Product1Symbol} / ${instruments[0].Product2Symbol}`;

  const ariaLabel = isBuySide
    ? t('Choose a Crypto to Buy')
    : t('Choose a Crypto to Sell');

  const menuNode =
    menuOpen &&
    menuPlacement &&
    createPortal(
      <div
        ref={menuRef}
        id={listboxId}
        className="dashboard-swap-pair__menu-root"
        role="listbox"
        aria-label={ariaLabel}
        style={{
          position: 'fixed',
          top: menuPlacement.top,
          left: menuPlacement.left,
          width: menuPlacement.width,
          maxHeight: menuPlacement.maxHeight,
          zIndex: 10050
        }}>
        {instruments.map(inst => {
          const selected =
            hasInstrument &&
            inst.InstrumentId === selectedInstrument.InstrumentId;
          return (
            <button
              key={inst.InstrumentId}
              type="button"
              role="option"
              aria-selected={selected}
              className={`dashboard-swap-pair__menu-option${
                selected ? ' dashboard-swap-pair__menu-option--selected' : ''
              }`}
              onClick={() => {
                onInstrumentSelect(inst.InstrumentId);
                setMenuOpen(false);
                triggerRef.current?.focus();
              }}>
              {inst.Product1Symbol} / {inst.Product2Symbol}
            </button>
          );
        })}
      </div>,
      document.body
    );

  return (
    <div className="dashboard-swap-pair">
      {menuNode}
      <div className="dashboard-swap-pair__box">
        <div className="dashboard-swap-pair__row dashboard-swap-pair__row--meta">
          <span className="dashboard-swap-pair__side-label">{t('From')}</span>
          <span className="dashboard-swap-pair__asset-pill">
            {product1?.iconFileName ? (
              <span className="dashboard-swap-pair__mini-icon">
                <ProductIconContainer
                  iconFileName={product1.iconFileName}
                  size={18}
                />
              </span>
            ) : (
              <span className="dashboard-swap-pair__dot dashboard-swap-pair__dot--p1" />
            )}
            <span className="dashboard-swap-pair__asset-name">{p1Name}</span>
          </span>
          <span className="dashboard-swap-pair__meta-value">
            {selectedInstrument.Symbol || ''}
          </span>
        </div>
        <div className="dashboard-swap-pair__row dashboard-swap-pair__row--control">
          <div
            ref={comboboxRef}
            className="dashboard-swap-pair__control-leading dashboard-swap-pair__control-leading--combobox">
            {product1?.iconFileName ? (
              <ProductIconContainer
                iconFileName={product1.iconFileName}
                size={28}
              />
            ) : (
              <span className="dashboard-swap-pair__token-placeholder">
                {p1Sym?.charAt(0)}
              </span>
            )}
            <button
              ref={triggerRef}
              type="button"
              className="dashboard-swap-pair__trigger"
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              aria-controls={listboxId}
              aria-label={ariaLabel}
              onClick={() => setMenuOpen(open => !open)}>
              <span className="dashboard-swap-pair__trigger-label">{pairLabel}</span>
              <span className="dashboard-swap-pair__trigger-chevron" aria-hidden="true" />
            </button>
          </div>
          <span className="dashboard-swap-pair__amount-readout">{quantityDisplay}</span>
        </div>
      </div>

      <div className="dashboard-swap-pair__swap-wrap">
        <button
          type="button"
          className="dashboard-swap-pair__swap-btn"
          onClick={onToggleSide}
          aria-label={t('Swap buy and sell')}>
          <SwapArrowsIcon />
        </button>
      </div>

      <div className="dashboard-swap-pair__box">
        <div className="dashboard-swap-pair__row dashboard-swap-pair__row--meta">
          <span className="dashboard-swap-pair__side-label">{t('To')}</span>
          <span className="dashboard-swap-pair__asset-pill">
            {product2?.iconFileName ? (
              <span className="dashboard-swap-pair__mini-icon">
                <ProductIconContainer
                  iconFileName={product2.iconFileName}
                  size={18}
                />
              </span>
            ) : (
              <span className="dashboard-swap-pair__dot dashboard-swap-pair__dot--p2" />
            )}
            <span className="dashboard-swap-pair__asset-name">{p2Name}</span>
          </span>
          <span className="dashboard-swap-pair__meta-value" aria-hidden="true" />
        </div>
        <div className="dashboard-swap-pair__row dashboard-swap-pair__row--control dashboard-swap-pair__row--readonly">
          <span className="dashboard-swap-pair__control-leading">
            {product2?.iconFileName ? (
              <ProductIconContainer
                iconFileName={product2.iconFileName}
                size={28}
              />
            ) : (
              <span className="dashboard-swap-pair__token-placeholder">
                {p2Sym?.charAt(0)}
              </span>
            )}
            <span className="dashboard-swap-pair__pair-leg">{p2Sym}</span>
          </span>
          <span className="dashboard-swap-pair__amount-readout">{priceDisplay}</span>
        </div>
      </div>
    </div>
  );
};

DashboardSwapPairPanel.propTypes = {
  instruments: PropTypes.array.isRequired,
  selectedInstrument: PropTypes.object.isRequired,
  onInstrumentSelect: PropTypes.func.isRequired,
  product1: PropTypes.object,
  product2: PropTypes.object,
  quantityDisplay: PropTypes.string.isRequired,
  priceDisplay: PropTypes.string.isRequired,
  onToggleSide: PropTypes.func.isRequired,
  isBuySide: PropTypes.bool.isRequired
};

DashboardSwapPairPanel.contextTypes = {
  t: PropTypes.func.isRequired
};

export default DashboardSwapPairPanel;
